import React, { useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/joy";
import NumericKeypad, { Dots } from "components/security/NumericKeypad";
import { theme } from "styles/theme";
import { useRecoilValue } from "recoil";
import { appBiometricEnabledAtom } from "recoil/appLockAtoms";
import { nativeBridge } from "utils/nativeBridge";
import { sendMessage } from "utils/webview";
import { isValidPin } from "utils/pinLock";
import { useNavigate } from "react-router-dom";
import { appLockApi } from "api/appLockApi";
import FloatingArea from "components/common/FloatingArea";
import CustomTypography from "components/common/CustomTypography";
import { useChannelTalkContext } from "contexts/ChannelTalkContext";
import { userAtom } from "recoil/userAtom";

export default function AppLockScreen() {
  const [pin, setPin] = React.useState("");
  const [error, setError] = React.useState<string>("");
  const [helpText, setHelpText] =
    React.useState<string>("비밀번호를 입력해주세요.");
  const [requesting, setRequesting] = React.useState(false);
  const [bioSupported, setBioSupported] = React.useState<boolean>(false);
  const bioEnabled = useRecoilValue(appBiometricEnabledAtom);
  const navigate = useNavigate();
  const [isBioRequesting, setIsBioRequesting] = useState(false);
  const mountedRef = React.useRef(true);
  const user = useRecoilValue(userAtom);

  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Layout measurement for vertical centering between top and keypad
  const floatingRef = React.useRef<HTMLDivElement | null>(null);
  const [availableHeight, setAvailableHeight] = React.useState<number | null>(
    null
  );

  const { openChat } = useChannelTalkContext();

  useEffect(() => {
    const measure = () => {
      const viewport =
        window.innerHeight || document.documentElement.clientHeight;
      const floatingH = floatingRef.current?.offsetHeight ?? 0;
      const avail = Math.max(0, viewport + 40 - floatingH);
      setAvailableHeight(avail);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
    };
  }, []);

  const onUnlock = React.useCallback(() => {
    try {
      sendMessage(JSON.stringify({ event: "applock_auth_complete" }));
    } catch {}
  }, []);

  // 생체 인증 지원 여부 체크
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const info = await nativeBridge.getBiometricStatus(3500);
        if (!mounted) return;
        setBioSupported(!!info?.available);
      } catch {
        if (!mounted) return;
        setBioSupported(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // 생체 인증 자동 실행 (설정 ON + 기기 지원)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!bioEnabled || !bioSupported) return;
      try {
        if (!cancelled) setIsBioRequesting(true);
        const res = await nativeBridge.requestBiometric({
          promptMessage: "잠금 해제를 위한 생채 인증",
          cancelLabel: "취소",
          disableDeviceFallback: false,
        });
        if (!cancelled && res?.success) {
          onUnlock();
        }
      } catch {
        // 실패/취소 시 PIN 입력으로 계속 진행
      } finally {
        if (!cancelled) setIsBioRequesting(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [bioEnabled, bioSupported, onUnlock]);

  const onInput = React.useCallback(
    (digit: string) => {
      if (requesting) return;
      setError("");
      if (!/^\d$/.test(digit)) return;
      setPin((prev) => (prev + digit).slice(0, 4));
    },
    [requesting]
  );

  const onBackspace = React.useCallback(() => {
    if (requesting) return;
    setError("");
    setPin((prev) => prev.slice(0, -1));
  }, [requesting]);

  const onClear = React.useCallback(() => {
    if (requesting) return;
    setError("");
    setPin("");
  }, [requesting]);

  // PIN 4자리 입력 시 서버 API로 검증 요청
  useEffect(() => {
    const verify = async () => {
      if (pin.length !== 4) return;
      if (!isValidPin(pin)) {
        setError("비밀번호는 숫자 4자리여야 합니다.");
        setPin("");
        return;
      }
      setRequesting(true);
      try {
        const res = await appLockApi.verifyPin(pin);
        if (res?.isSuccess) {
          onUnlock();
        } else {
          setHelpText("비밀번호가 일치하지 않아요.");
          setPin("");
        }
      } catch (e) {
        setError("확인 중 오류가 발생했습니다. 다시 시도하세요.");
        setPin("");
      } finally {
        setRequesting(false);
      }
    };
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  return (
    <Box
      sx={{
        px: 3,
        minHeight: "100vh",
        boxSizing: "border-box",
        width: "100%",
        backgroundColor: theme.vars.palette.Background.Level1,
      }}
    >
      <Stack
        spacing={4}
        alignItems="center"
        justifyContent="center"
        sx={{ minHeight: availableHeight ? `${availableHeight}px` : "60vh" }}
      >
        <Stack spacing={"20px"} alignItems="center">
          <Typography
            variant={"Body"}
            sx={{
              fontWeight: 500,
              color: theme.vars.palette.Content.Medium,
            }}
          >
            {helpText}
          </Typography>
          <Dots count={pin.length} />
        </Stack>
      </Stack>
      <FloatingArea noDivider={true} fullWidth={true} ref={floatingRef}>
        <Box sx={{ mb: 3 }}>
          <CustomTypography
            variant={"Caption"}
            sx={{
              fontWeight: 500,
              color: (theme) => theme.vars.palette.Content.Medium,
            }}
          >
            {"비밀번호를 잊으셨나요? "}
            <Box
              component="a"
              onClick={(e) => {
                e.preventDefault();
                openChat();
              }}
              sx={{
                textDecoration: "underline",
                fontWeight: 700,
                color: (theme) => theme.vars.palette.Content.Highlight,
                cursor: "pointer",
              }}
            >
              문의하기
            </Box>
          </CustomTypography>
        </Box>
        <NumericKeypad
          onInput={onInput}
          onBackspace={onBackspace}
          onClear={onClear}
        />
      </FloatingArea>

      {isBioRequesting && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            zIndex: 9999,
          }}
        />
      )}
    </Box>
  );
}
